import { useState, useEffect, useMemo } from 'react';
import platform from 'utils/platform';

/**
 * Snapshot del entorno de ejecución.
 *
 * Los flags de host (isNativeApp/isIOS/isAndroid/has*) son estáticos por sesión.
 * deviceType (e isMobile/isTablet/isDesktop) e isStandalone reaccionan a
 * resize de ventana y cambios de display-mode (instalación PWA).
 * En WebView nativo el layout es fijo, así que se omiten los listeners.
 */
const usePlatform = () => {
  const [deviceType, setDeviceType] = useState(platform.deviceType);
  const [isStandalone, setIsStandalone] = useState(platform.isStandalone);

  useEffect(() => {
    if (platform.isNativeApp) return undefined;

    let frame = null;
    const handleResize = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setDeviceType(platform.deviceType));
    };
    const standaloneMql = window.matchMedia('(display-mode: standalone)');
    const handleDisplayMode = () => setIsStandalone(platform.isStandalone);

    window.addEventListener('resize', handleResize);
    standaloneMql.addEventListener('change', handleDisplayMode);
    return () => {
      window.removeEventListener('resize', handleResize);
      standaloneMql.removeEventListener('change', handleDisplayMode);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  const staticFlags = useMemo(() => ({
    isNativeApp: platform.isNativeApp,
    isWeb: platform.isWeb,
    isIOS: platform.isIOS,
    isAndroid: platform.isAndroid,
    isMobileBrowser: platform.isMobileBrowser,
    isTabletBrowser: platform.isTabletBrowser,
    isDesktopBrowser: platform.isDesktopBrowser,
    hasCamera: platform.hasCamera,
    hasBiometrics: platform.hasBiometrics,
    hasPushSupport: platform.hasPushSupport,
  }), []);

  return {
    ...staticFlags,
    isStandalone,
    deviceType,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
  };
};

export default usePlatform;
